// src/features/medicine-inventory/pages/MedicineIssue.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash, FaPrescription, FaFileMedical } from 'react-icons/fa';
import { useInventory } from '../hooks/useInventory'; // Changed from '../../context/InventoryContext'
import PatientSearch from '../components/patient/PatientSearch';
import VisitSelector from '../components/patient/VisitSelector';
import PrescriptionSelector from '../components/patient/PrescriptionSelector';
import { generatePrescriptionNumber } from '../utils/helpers'; // Changed from '../../utils/helpers'
import toast from 'react-hot-toast';

const MedicineIssue = () => {
  const navigate = useNavigate();
  const { medicines, addStockMovement } = useInventory();
  
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [issueType, setIssueType] = useState('PRESCRIPTION'); // 'PRESCRIPTION' or 'MANUAL'
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    visitId: '',
    prescriptionId: '',
    doctorName: '',
    remarks: '',
    medicines: []
  });
  const [submitting, setSubmitting] = useState(false);

  // Update form data when selections change
  useEffect(() => {
    if (selectedPatient) {
      setFormData(prev => ({
        ...prev,
        patientId: selectedPatient.id,
        patientName: selectedPatient.name
      }));
    }
  }, [selectedPatient]);

  useEffect(() => {
  if (!selectedPatient) {
    setSelectedVisit(null);
    setSelectedPrescription(null);

    setFormData(prev => ({
      ...prev,
      visitId: '',
      prescriptionId: '',
      medicines: []
    }));
  }
}, [selectedPatient]);

  useEffect(() => {
    if (selectedVisit) {
      setFormData(prev => ({
        ...prev,
        visitId: selectedVisit.id,
        doctorName: selectedVisit.doctorName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        visitId: '',
        doctorName: prev.doctorName // Keep doctor name if manually entered
      }));
    }
  }, [selectedVisit]);

  useEffect(() => {
    if (selectedPrescription) {
      setFormData(prev => ({
        ...prev,
        prescriptionId: selectedPrescription.id,
        doctorName: selectedPrescription.doctorName,
        medicines: selectedPrescription.items.map(med => ({
          medicineId: med.medicine.id,
          medicineName: med.medicine.name,
          quantity: calculateTablets(med.frequency,med.duration),
          unitPrice: getMedicinePrice(med.medicineId),
          dosage: med.dosage,
          instructions: med.frequency+ ' , '+med.duration+' , '+med.instructions,
          maxStock: med.total_available
        }))
      }));
      setIssueType('PRESCRIPTION');
    } else {
      setFormData(prev => ({
        ...prev,
        prescriptionId: '',
        medicines: issueType === 'MANUAL' ? prev.medicines : []
      }));
    }
  }, [selectedPrescription]);

  const getMedicineIssueQuantity = (frequency,duration) => {
return frequency*duration;
  };
  function calculateTablets(frequency, duration) {

  const freqMap = {
    "Once daily": 1,
    "Twice daily": 2,
    "Thrice daily": 3,
    "Four times daily": 4,
    "Every 6 hours": 4,
    "Every 8 hours": 3,
    "Every 12 hours": 2,
    "At bedtime":1,
    "Once daily before meals":1,
    "As needed":2
  };

  const perDay = freqMap[frequency] || 1;

  const days = parseInt(duration.match(/\d+/)?.[0] || 1);

  return perDay * days;
}

  const getMedicinePrice = (medicineId) => {
    const medicine = medicines.find(m => m.id === medicineId);
    return medicine?.price || 0;
  };

  const getMedicineStock = (medicineId) => {
    const medicine = medicines.find(m => m.id === medicineId);
    return medicine?.currentStock || 0;
  };

  const handleAddMedicine = () => {
    setFormData(prev => ({
      ...prev,
      medicines: [...prev.medicines, {
        medicineId: '',
        quantity: 1,
        unitPrice: '',
        dosage: '',
        instructions: '',
        maxStock: 0
      }]
    }));
  };

  const handleRemoveMedicine = (index) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.filter((_, i) => i !== index)
    }));
  };

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...formData.medicines];
    
    if (field === 'medicineId') {
      const selectedMedicine = medicines.find(m => m.id === value);
      updatedMedicines[index] = {
        ...updatedMedicines[index],
        medicineId: value,
        medicineName: selectedMedicine?.name || '',
        unitPrice: selectedMedicine?.price || '',
        maxStock: selectedMedicine?.currentStock || 0
      };
    } else {
      updatedMedicines[index][field] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      medicines: updatedMedicines
    }));
  };

  const validateStock = () => {
    for (const item of formData.medicines) {
      const medicine = medicines.find(m => m.id === item.medicineId);
      if (medicine && item.quantity > medicine.currentStock) {
        toast.error(`Insufficient stock for ${medicine.name}. Available: ${medicine.currentStock}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    if (formData.medicines.length === 0) {
      toast.error('Please add at least one medicine');
      return;
    }

    if (!validateStock()) {
      return;
    }

    setSubmitting(true);
    
    try {
      const issueData = {
        type: 'MULTI_ISSUE',
        patient_id: selectedPatient.id,
       // patientName: selectedPatient.name,
        visit_id: selectedVisit?.id,
        prescription_id: selectedPrescription?.id,
        doctorName: formData.doctorName || selectedVisit?.doctorName,
        remarks: formData.remarks,
        medicines: formData.medicines.map(item => ({
          medicine_id: item.medicineId,
          quantity: parseInt(item.quantity),
          // unitPrice: parseFloat(item.unitPrice),
           dosage: item.dosage,
          instructions: item.instructions
        }))
      };

      await addStockMovement(issueData);
      toast.success('Medicines issued successfully');
      navigate('/stock');
    } catch (error) {
      console.error('Failed to issue medicines:', error);
      toast.error('Failed to issue medicines');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return formData.medicines.reduce((sum, item) => {
      return sum + (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0));
    }, 0);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-center">
        <button
          onClick={() => navigate('/stock')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <FaArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Issue Medicines</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h2>
          <PatientSearch
            onSelectPatient={setSelectedPatient}
            selectedPatient={selectedPatient}
          />
        </div>

        {selectedPatient && (
          <>
            {/* Visit and Prescription Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Visit & Prescription</h2>
              
              <div className="grid grid-cols-2 gap-6">
                <VisitSelector
                  patientId={selectedPatient.id}
                  onSelectVisit={setSelectedVisit}
                  selectedVisit={selectedVisit}
                />
                
                <PrescriptionSelector
                  patientId={selectedPatient.id}
                  visitId={selectedVisit?.id}
                  onSelectPrescription={setSelectedPrescription}
                  selectedPrescription={selectedPrescription}
                />
              </div>

              {!selectedPrescription && (
                <div className="mt-4">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={issueType === 'MANUAL'}
                      onChange={(e) => {
                        setIssueType(e.target.checked ? 'MANUAL' : 'PRESCRIPTION');
                        if (!e.target.checked) {
                          setFormData(prev => ({ ...prev, medicines: [] }));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Manual medicine selection (without prescription)
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Doctor and Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doctor Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.doctorName}
                    onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
                    placeholder="Enter doctor name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chief Complaint
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="problems of patient"
                  />
                </div>
              </div>
            </div>

            {/* Medicines List */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Medicines</h2>
                {(issueType === 'MANUAL' || !selectedPrescription) && (
                  <button
                    type="button"
                    onClick={handleAddMedicine}
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    <FaPlus className="mr-2" /> Add Medicine
                  </button>
                )}
              </div>

              {formData.medicines.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {selectedPrescription ? (
                    <p>No medicines found in this prescription</p>
                  ) : (
                    <p>Click "Add Medicine" to start adding medicines</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.medicines.map((item, index) => {
                    const medicine = medicines.find(m => m.id === item.medicineId);
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-12 gap-4">
                          <div className="col-span-5">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Medicine
                            </label>
                            {selectedPrescription ? (
                              <div className="py-2">
                                <p className="font-medium text-gray-900">{item.medicineName}</p>
                                <p className="text-xs text-gray-500">Stock: {item.maxStock}</p>
                              </div>
                            ) : (
                              <select
                                required
                                className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={item.medicineId}
                                onChange={(e) => handleMedicineChange(index, 'medicineId', e.target.value)}
                              >
                                <option value="">Select Medicine</option>
                                {medicines.map(med => (
                                  //console.log(med),
                                  <option key={med.id} value={med.id}>
                                    {med.name}-{med.strength} - Stock: {med.total_available} {med.unit}s
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>

                          <div className="col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Dosage
                            </label>
                            <input
                              type="text"
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={item.dosage || ''}
                              onChange={(e) => handleMedicineChange(index, 'dosage', e.target.value)}
                              placeholder="e.g., 500mg"
                            />
                          </div>

                          <div className="col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Quantity
                            </label>
                            <input
                              type="number"
                              required
                              min="1"
                              max={item.total_available}
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={item.quantity}
                              onChange={(e) => handleMedicineChange(index, 'quantity', parseInt(e.target.value))}
                            />
                          </div>

                          {/* <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Unit Price ($)
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                              value={item.unitPrice}
                              readOnly
                            />
                          </div> */}

                          {/* <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Total
                            </label>
                            <div className="py-2 font-medium text-gray-900">
                              ${(item.quantity * item.unitPrice).toFixed(2)}
                            </div>
                          </div> */}

                          {!selectedPrescription && (
                            <div className="col-span-1 flex items-end">
                              <button
                                type="button"
                                onClick={() => handleRemoveMedicine(index)}
                                className="text-red-600 hover:text-red-800 mb-2"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="mt-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Instructions
                          </label>
                          <input
                            type="text"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={item.instructions || ''}
                            onChange={(e) => handleMedicineChange(index, 'instructions', e.target.value)}
                            placeholder="e.g., Take after meals"
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Total Amount:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        ${calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div> */}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/inventory/stock-movements')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting || formData.medicines.length === 0}
              >
                {submitting ? 'Processing...' : 'Issue Medicines'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default MedicineIssue;